import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Trash2 } from 'lucide-react';
import { charitySummary, adminAddCharity, adminDeleteCharity, listCompetitions } from '@/lib/api';
import { formatGBPfromPence, formatDateLong } from '@/lib/format';

const AdminCharity = () => {
  const [data, setData] = useState({ total_pence: 0, contributions: [] });
  const [comps, setComps] = useState([]);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    charity_name: '',
    amount_gbp: '',
    competition_id: '',
    contributed_at: new Date().toISOString().slice(0, 10),
    notes: '',
  });

  const load = () => {
    charitySummary().then(setData).catch(() => {});
    listCompetitions?.().then((d) => setComps(d.items || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    const amount = Number(form.amount_gbp);
    if (!form.charity_name.trim() || !amount || amount <= 0) { toast.error('Please give a charity name and a positive amount.'); return; }
    setSaving(true);
    try {
      await adminAddCharity({
        charity_name: form.charity_name.trim(),
        amount_pence: Math.round(amount * 100),
        competition_id: form.competition_id || null,
        contributed_at: form.contributed_at ? new Date(form.contributed_at + 'T12:00:00Z').toISOString() : null,
        notes: form.notes.trim() || null,
      });
      toast.success('Contribution logged.');
      setForm({ ...form, charity_name: '', amount_gbp: '', notes: '' });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not log contribution.');
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Remove this contribution from the public log? This is an audit trail — only do this to correct genuine errors.')) return;
    try {
      await adminDeleteCharity(id);
      toast.success('Contribution removed.');
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Delete failed.');
    }
  };

  const inputCls = 'w-full h-11 rounded-xl bg-[color:var(--pd-bg)] border border-pd px-3 text-sm focus:border-[color:var(--pd-accent-yellow)] focus:outline-none';

  return (
    <div data-testid="admin-charity-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-4xl leading-none">Charity log</h1>
          <p className="mt-2 text-sm text-muted-sage">
            Log every donation as it is made. This directly populates the public /giving-back page.
          </p>
        </div>
        <div className="bg-surface border border-pd rounded-xl px-4 py-3">
          <div className="font-mono text-[10px] text-muted-sage uppercase">Donated so far</div>
          <div className="font-display text-2xl text-[color:var(--pd-accent-yellow)]">{formatGBPfromPence(data.total_pence || 0)}</div>
        </div>
      </div>

      <form onSubmit={submit} className="mt-6 bg-surface border border-pd rounded-2xl p-5 grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="admin-charity-form">
        <label className="block">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-1">Charity name</div>
          <input required type="text" value={form.charity_name} onChange={(e) => setForm({ ...form, charity_name: e.target.value })} className={inputCls} data-testid="charity-name" />
        </label>
        <label className="block">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-1">Amount (£)</div>
          <input required type="number" min={0} step={0.01} value={form.amount_gbp} onChange={(e) => setForm({ ...form, amount_gbp: e.target.value })} className={inputCls} data-testid="charity-amount" />
        </label>
        <label className="block">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-1">Competition (optional)</div>
          <select value={form.competition_id} onChange={(e) => setForm({ ...form, competition_id: e.target.value })} className={inputCls} data-testid="charity-competition">
            <option value="">— Not linked to a competition —</option>
            {comps.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
          </select>
        </label>
        <label className="block">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-1">Date</div>
          <input type="date" value={form.contributed_at} onChange={(e) => setForm({ ...form, contributed_at: e.target.value })} className={inputCls} data-testid="charity-date" />
        </label>
        <label className="block md:col-span-2">
          <div className="font-mono text-[10px] uppercase tracking-widest text-muted-sage mb-1">Notes (optional, internal only)</div>
          <input type="text" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className={inputCls} data-testid="charity-notes" />
        </label>
        <div className="md:col-span-2 flex justify-end">
          <button type="submit" disabled={saving} className="bg-[color:var(--pd-accent-yellow)] text-[#14251c] rounded-xl px-4 py-2.5 text-sm font-medium disabled:opacity-60" data-testid="charity-submit">
            {saving ? 'Logging…' : 'Log contribution'}
          </button>
        </div>
      </form>

      <div className="mt-6">
        <div className="font-mono text-[10px] text-muted-sage uppercase tracking-widest">Log</div>
        {(data.contributions || []).length === 0 ? (
          <div className="mt-3 text-sm text-muted-sage">No contributions logged yet.</div>
        ) : (
          <div className="mt-3 rounded-2xl border border-pd bg-surface overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[color:var(--pd-bg)]">
                  <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-muted-sage">
                    <th className="px-4 py-3">Date</th>
                    <th className="px-4 py-3">Charity</th>
                    <th className="px-4 py-3">Competition</th>
                    <th className="px-4 py-3 text-right">Amount</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {data.contributions.map((c) => (
                    <tr key={c.id} className="border-t border-pd" data-testid="admin-charity-row">
                      <td className="px-4 py-3 text-muted-sage">{formatDateLong(c.contributed_at)}</td>
                      <td className="px-4 py-3">{c.charity_name}</td>
                      <td className="px-4 py-3 text-muted-sage">{c.competition_title || '—'}</td>
                      <td className="px-4 py-3 text-right font-mono text-[color:var(--pd-accent-yellow)]">{formatGBPfromPence(c.amount_pence)}</td>
                      <td className="px-4 py-3 text-right">
                        <button onClick={() => remove(c.id)} aria-label="Delete contribution" className="text-red-300 hover:text-red-200" data-testid={`charity-delete-${c.id}`}>
                          <Trash2 size={14} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCharity;
